/**
 * Dialog para transferir conversa para um departamento
 *
 * Permite selecionar um departamento destino e opcionalmente
 * remover o agente atual (volta para fila do departamento).
 *
 * v2.0.0 - 2026-06-03 - Redesign visual "Dark Executive":
 *   - Header refinado com X custom e borda inferior sutil
 *   - Barra de busca para filtrar departamentos em tempo real
 *   - Lista compacta com ícone, badges (Atual/Auto) e indicador de seleção
 *   - Opção "Remover agente" em card destacado
 *   - Nota em campo coerente com o resto do sistema
 *   - Footer com fundo `bg-muted/30` e botões padronizados
 *   - 100% baseado em tokens semânticos do design system (light/dark)
 *   - Lógica de transferência, gatilhos e estados preservada integralmente
 */

import { useState, useEffect, useMemo } from 'react';
import type { Database } from '@/integrations/supabase/types';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2,
  Building,
  Check,
  Zap,
  X,
  Search,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger para este módulo
const logger = createLogger('TransferToDepartmentDialog');

interface Department {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  auto_distribution_enabled: boolean | null;
  distribution_method: string | null;
}

interface TransferToDepartmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  currentDepartmentId?: string | null;
  onSuccess?: () => void;
}

export function TransferToDepartmentDialog({
  open,
  onOpenChange,
  conversationId,
  currentDepartmentId,
  onSuccess,
}: TransferToDepartmentDialogProps) {
  const { currentTenant, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [note, setNote] = useState('');
  const [removeAgent, setRemoveAgent] = useState(true); // Por padrão, remove agente
  const [transferring, setTransferring] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Carrega departamentos quando o dialog abre
  useEffect(() => {
    if (open && currentTenant?.id) {
      fetchDepartments();
      setSelectedDepartment(null);
      setNote('');
      setRemoveAgent(true);
      setSearchTerm('');
    }
  }, [open, currentTenant?.id]);

  // Busca departamentos ativos do tenant
  const fetchDepartments = async () => {
    if (!currentTenant?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name, description, is_active, auto_distribution_enabled, distribution_method')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      // Não filtra mais o departamento atual - permite redistribuição no mesmo departamento
      // quando a opção "Remover agente atual" está marcada
      setDepartments(data || []);

      logger.debug('Departamentos carregados', { count: (data || []).length });
    } catch (error) {
      logger.error('Erro ao carregar departamentos', error);
      toast.error('Erro ao carregar departamentos');
    } finally {
      setLoading(false);
    }
  };

  // Filtra departamentos por termo de busca (case-insensitive)
  const filteredDepartments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return departments;
    return departments.filter(
      (d) =>
        d.name.toLowerCase().includes(term) ||
        (d.description || '').toLowerCase().includes(term)
    );
  }, [departments, searchTerm]);

  // Handler para transferir
  const handleTransfer = async () => {
    if (!selectedDepartment || !user) return;

    setTransferring(true);
    try {
      logger.info('Transferindo conversa para departamento', {
        conversationId,
        departmentId: selectedDepartment.id,
        departmentName: selectedDepartment.name,
        removeAgent,
      });

      // 1. Registrar transferência na tabela conversation_transfers
      const { error: transferError } = await supabase
        .from('conversation_transfers')
        .insert({
          conversation_id: conversationId,
          from_agent_id: user.id,
          from_department_id: currentDepartmentId || null,
          to_department_id: selectedDepartment.id,
          note: note || null,
          status: 'accepted', // Transferência para departamento é imediata
          responded_at: new Date().toISOString(),
        });

      if (transferError) throw transferError;

      // 2. Atualizar conversa com novo departamento e pausar bot automaticamente
      // Tipo seguro do Supabase para update de conversas
      const updateData: Database['public']['Tables']['conversations']['Update'] = {
        department_id: selectedDepartment.id,
        // Pausa bot automaticamente ao transferir para departamento manualmente
        bot_paused: true,
        bot_paused_at: new Date().toISOString(),
        bot_paused_by: user.id,
      };

      // Se marcou para remover agente, limpa assigned_to e volta status para pending
      if (removeAgent) {
        updateData.assigned_to = null;
        updateData.status = 'pending';
      }

      const { error: convError } = await supabase
        .from('conversations')
        .update(updateData)
        .eq('id', conversationId);

      if (convError) throw convError;

      // ===== VERIFICAÇÃO DE GATILHOS MANUAIS (departamento) =====
      // Chama Edge Function de forma assíncrona (não bloqueia a UI)
      logger.info('Verificando gatilhos de departamento', {
        conversationId,
        newDepartmentId: selectedDepartment.id,
        oldDepartmentId: currentDepartmentId,
      });

      supabase.functions
        .invoke('check-manual-triggers', {
          body: {
            event_type: 'department_changed',
            conversation_id: conversationId,
            new_department_id: selectedDepartment.id,
            old_department_id: currentDepartmentId || null,
          },
        })
        .then(({ data, error: triggerError }) => {
          if (triggerError) {
            logger.warn('Erro ao verificar gatilhos de departamento', triggerError);
          } else {
            logger.info('Gatilhos de departamento verificados', data);
          }
        })
        .catch((err) => {
          logger.warn('Exceção ao chamar check-manual-triggers', err);
        });
      // ===== FIM DA VERIFICAÇÃO DE GATILHOS =====

      // NOTA IMPORTANTE: A distribuição automática é executada pelo trigger trg_auto_distribute_after_transfer
      // que dispara automaticamente quando department_id é alterado com assigned_to = null.
      // NÃO chamar auto_distribute_conversation diretamente aqui para evitar distribuição duplicada!
      // O trigger já cuida de toda a lógica de round robin/least busy corretamente.
      if (removeAgent && selectedDepartment.auto_distribution_enabled) {
        logger.info('Distribuição automática será executada pelo trigger do banco de dados', {
          conversationId,
          departmentId: selectedDepartment.id,
          distributionMethod: selectedDepartment.distribution_method,
        });
      }

      logger.info('Conversa transferida com sucesso');
      toast.success(`Conversa transferida para ${selectedDepartment.name}`);

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('Erro ao transferir conversa', error);
      toast.error('Erro ao transferir conversa');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[400px] p-0 gap-0 overflow-hidden"
        hideCloseButton
      >
        {/* ============== HEADER ============== */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between border-b border-border/60">
          <div className="min-w-0 flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Building className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground tracking-tight">
                Transferir para Departamento
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Selecione o departamento destino para esta conversa.
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => onOpenChange(false)}
            title="Fechar"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* ============== SEARCH ============== */}
        {!loading && departments.length > 0 && (
          <div className="px-5 pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar departamento..."
                className="pl-9 h-9 bg-muted/40 border-border/60 focus-visible:ring-ring/30"
              />
            </div>
          </div>
        )}

        {/* ============== CONTEÚDO ============== */}
        <div className="px-5 py-3 overflow-y-auto max-h-[368px] space-y-3">
          {/* ===== Lista de departamentos ===== */}
          <div>
            <h3 className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-2">
              Departamentos {departments.length > 0 && `· ${departments.length}`}
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : departments.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <div className="mx-auto w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Building className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-sm">
                  Nenhum departamento disponível.
                </p>
              </div>
            ) : filteredDepartments.length === 0 ? (
              <div className="text-center py-5 text-sm text-muted-foreground">
                Nenhum departamento corresponde a "{searchTerm}".
              </div>
            ) : (
              <div className="flex flex-col gap-1 max-h-[208px] overflow-y-auto pr-1">
                {filteredDepartments.map((dept) => {
                  const isSelected = selectedDepartment?.id === dept.id;
                  return (
                    <button
                      key={dept.id}
                      type="button"
                      onClick={() => setSelectedDepartment(dept)}
                      className={cn(
                        'w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all border',
                        isSelected
                          ? 'bg-muted/60 border-border ring-1 ring-ring/30'
                          : 'border-transparent hover:bg-muted/40 hover:border-border/60'
                      )}
                    >
                      <div className="flex items-center justify-center h-7 w-7 rounded-md bg-muted text-muted-foreground shrink-0">
                        <Building className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground truncate">
                            {dept.name}
                          </p>
                          {/* Badge indicando departamento atual */}
                          {dept.id === currentDepartmentId && (
                            <Badge variant="outline" className="text-[10px] px-1.5 h-5">
                              Atual
                            </Badge>
                          )}
                          {dept.auto_distribution_enabled && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="secondary"
                                  className="h-5 gap-1 text-[10px] px-1.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                >
                                  <Zap className="h-3 w-3" />
                                  Auto
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>Distribuição automática ativa</p>
                                <p className="text-xs text-muted-foreground">
                                  {dept.distribution_method === 'round_robin'
                                    ? 'Rodízio'
                                    : 'Menos ocupado'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {dept.description && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {dept.description}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ===== Opção remover agente ===== */}
          {departments.length > 0 && (
            <label
              htmlFor="remove-agent"
              className="flex items-start gap-3 p-2.5 rounded-lg border border-border/60 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
            >
              <Checkbox
                id="remove-agent"
                checked={removeAgent}
                onCheckedChange={(checked) => setRemoveAgent(checked === true)}
                className="mt-0.5"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">
                  Remover agente atual
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  A conversa volta para a fila do departamento destino.
                </p>
              </div>
            </label>
          )}

          {/* ===== Nota de transferência ===== */}
          {departments.length > 0 && (
            <div className="space-y-2">
              <Label
                htmlFor="transfer-dept-note"
                className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest"
              >
                Nota de transferência (opcional)
              </Label>
              <Textarea
                id="transfer-dept-note"
                placeholder="Adicione contexto para o departamento..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="resize-none bg-muted/40 border-border/60 focus-visible:ring-ring/30"
              />
              <p className="text-xs text-muted-foreground">
                Esta nota será visível apenas para a equipe.
              </p>
            </div>
          )}
        </div>

        {/* ============== FOOTER ============== */}
        <div className="px-5 py-3 bg-muted/30 border-t border-border/60 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={transferring}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!selectedDepartment || transferring}
            className="shadow-sm"
          >
            {transferring ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Transferindo...
              </>
            ) : (
              'Transferir'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
